import React, { useState, useEffect } from 'react';
import {
  useToaster,
  Message,
  Loader,
  Stack,
  Form,
  SelectPicker,
  Checkbox,
  Schema,
  IconButton,
  List,
  FlexboxGrid
} from 'rsuite';
import { VscSave } from 'react-icons/vsc';
import { Icon } from '@rsuite/icons';
import { MdTaskAlt } from 'react-icons/md';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { UpdatePermissionsByUser, getPermmissionsByUser } from '@/services/permission.service';
import { getSteps, getAllTasks } from '@/services/step.service';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { useParams } from 'react-router-dom';
import { LuWorkflow } from 'react-icons/lu';
import { TbSubtask } from 'react-icons/tb';
import { RiUserShared2Line } from 'react-icons/ri';

const { StringType } = Schema.Types;

const styleCenter = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '60px'
};

const slimText = {
  fontSize: '0.666em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const model = Schema.Model({
  name: StringType().isRequired('This field is required.'),
  grade: StringType().isRequired('This field is required.'),
  email: StringType()
    .isEmail('Please enter a valid email address.')
    .isRequired('This field is required.')
});

const UserPermissions = () => {
  const toaster = useToaster();

  const permissions = useAppSelector((state: any) => state.permissions.data);
  const loading = useAppSelector((state: any) => state.permissions.loading);

  const success = useAppSelector((state: any) => state.permissions.success);
  const error = useAppSelector((state: any) => state.permissions.error);

  const currentUser = useAppSelector((state: any) => state.auth.currentUser);

  const [permissionsSteps, setPermissionsSteps] = useState(permissions?.stepsValidation);
  const [permissionsTasks, setPermissionsTasks] = useState(permissions?.tasks);
  const [steps, setSteps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [submitForOthers, setSubmitForOthers] = useState(false);

  const dispatch = useAppDispatch();
  const { id } = useParams();

  // const [value, setValue] = useState(['R', 'W']);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(getPermmissionsByUser(id));
      // await dispatch(getWorkflowSteps('initial'));
      const stepsRes = await getSteps('validation');
      setSteps(
        stepsRes?.map(item => {
          return { value: item._id, label: item.name };
        })
      );
      const tasksRes = await getAllTasks();
      setTasks(
        tasksRes?.map(item => {
          return { value: item._id, label: item.name };
        })
      );
    };

    fetchData();
  }, []);

  useEffect(() => {
    setPermissionsSteps(permissions?.stepsValidation);
    setPermissionsTasks(permissions?.tasks);
    setSubmitForOthers(permissions.submitForOthers);
  }, [permissions]);

  useEffect(() => {
    if (success) {
      toaster.push(
        <Message closable showIcon type="success">
          {success}
        </Message>,
        {
          placement: 'topCenter'
        }
      );
    }

    if (error) {
      toaster.push(
        <Message closable showIcon type="error">
          {error}
        </Message>,
        {
          placement: 'topCenter'
        }
      );
    }
  }, [success, error]);

  const handleAddRowStep = () => {
    // const newStep = {
    //   step: null,
    //   permissions: { R: false, W: false, V: false }
    // };

    // if (newStep.step !== null && newStep.step !== undefined) {
    //   const updatedPermissionsSteps = [...permissionsSteps, newStep];
    //   console.log(updatedPermissionsSteps);
    //   setPermissionsSteps([updatedPermissionsSteps]);
    // } else {
    //   setPermissionsSteps([newStep]);
    // }

    if (permissionsSteps !== undefined) {
      setPermissionsSteps([
        ...permissionsSteps,
        {
          step: null,
          permissions: { R: false, W: false, V: false }
        }
      ]);
    } else {
      setPermissionsSteps([
        {
          step: null,
          permissions: { R: false, W: false, V: false }
        }
      ]);
    }
  };

  const handleRemoveRowStep = index => {
    const values = [...permissionsSteps];
    values.splice(index, 1);
    setPermissionsSteps(values);
  };

  const handleChange = (index, field) => {
    const newArr: any = [...permissionsSteps];
    newArr[index].permissions[field] = !newArr[index].permissions[field];
    setPermissionsSteps(newArr);
  };

  const handleChangeStep = (index, event) => {
    const id = event;
    const newArr = [...permissionsSteps];
    newArr[index].step = id;
    setPermissionsSteps(newArr);
  };

  const handleChangeTaskPermission = (index, field) => {
    const newArr: any = [...permissionsTasks];
    newArr[index].permissions[field] = !newArr[index].permissions[field];
    setPermissionsTasks(newArr);
  };

  const handleChangeTask = (index, event) => {
    const id = event;
    const newArr = [...permissionsTasks];
    newArr[index].task = id;
    setPermissionsTasks(newArr);
  };

  const handleAddRowTask = () => {
    if (permissionsTasks !== undefined) {
      setPermissionsTasks([
        ...permissionsTasks,
        {
          task: null,
          permissions: { R: false, W: false, M: false }
        }
      ]);
    } else {
      setPermissionsTasks([
        {
          task: null,
          permissions: { R: false, W: false, M: false }
        }
      ]);
    }
  };

  const handleRemoveRowTask = index => {
    const values = [...permissionsTasks];
    values.splice(index, 1);
    setPermissionsTasks(values);
  };

  const handleSave = async () => {
    await dispatch(
      UpdatePermissionsByUser(id, {
        stepsValidation: permissionsSteps,
        tasks: permissionsTasks,
        submitForOthers: submitForOthers
      })
    );
  };

  return (
    <>
      <Form
        fluid
        model={model}
        disabled={currentUser.admin != 'super' && currentUser.admin != 'facility'}
      >
        <Stack style={{ marginTop: 50, marginBottom: 10 }} spacing={5}>
          <RiUserShared2Line /> <h6 className="title">Submit mission request for other</h6>{' '}
          <Checkbox
            onChange={() => {
              setSubmitForOthers(!submitForOthers);
            }}
            checked={submitForOthers}
          ></Checkbox>
        </Stack>
        <Stack style={{ marginTop: 50, marginBottom: 10 }} spacing={5}>
          <LuWorkflow /> <h6 className="title">Manage steps permissions</h6>
        </Stack>
        {loading ? (
          <center>
            <Loader size="md" />{' '}
          </center>
        ) : (
          <>
            <List hover>
              {permissionsSteps?.map((item, index) => (
                <List.Item key={index} index={index + 1}>
                  <FlexboxGrid>
                    <FlexboxGrid.Item colspan={6} style={styleCenter}>
                      <div style={{ textAlign: 'left' }}>
                        <div style={slimText}>Step</div>
                        {/* <SelectPicker
                      data={steps}
                      value={item?.step}
                      onChange={e => {
                        handleChangeStep(index, e);
                      }}
                      placement="auto"
                      style={{ width: 250 }}
                      disabledItemValues={Array.from(permissionsSteps, ({ step }) => step)}
                    /> */}
                        <Form.Control
                          name="selectPicker"
                          accepter={SelectPicker}
                          data={steps}
                          value={item?.step}
                          onChange={e => {
                            handleChangeStep(index, e);
                          }}
                          placement="auto"
                          style={{ width: 250 }}
                          disabledItemValues={
                            permissionsSteps?.length > 0
                              ? Array.from(permissionsSteps, ({ step }) => step)
                              : []
                          }
                        />
                      </div>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item colspan={10} style={styleCenter}>
                      <div style={{ textAlign: 'left' }}>
                        <div style={slimText}>Access rights</div>

                        <Stack spacing={10}>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="R"
                            onChange={() => {
                              handleChange(index, 'R');
                            }}
                            checked={item.permissions && item.permissions['R']}
                          >
                            Read
                          </Form.Control>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="W"
                            onChange={() => {
                              handleChange(index, 'W');
                            }}
                            checked={item.permissions && item.permissions['W']}
                          >
                            Write
                          </Form.Control>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="V"
                            onChange={() => {
                              handleChange(index, 'V');
                            }}
                            checked={item.permissions && item.permissions['V']}
                          >
                            Validate
                          </Form.Control>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="M"
                            onChange={() => {
                              handleChange(index, 'M');
                            }}
                            checked={item.permissions && item.permissions['M']}
                          >
                            Manage owners
                          </Form.Control>
                        </Stack>
                        {/* <Checkbox
                        value="R"
                        onChange={() => {
                          handleChange(index, 'R');
                        }}
                        checked={item.permissions['R']}
                      >
                        Read
                      </Checkbox>
                      <Checkbox
                        value="W"
                        onChange={() => {
                          handleChange(index, 'W');
                        }}
                        checked={item.permissions['W']}
                      >
                        Write
                      </Checkbox>
                      <Checkbox
                        value="V"
                        onChange={() => {
                          handleChange(index, 'V');
                        }}
                        checked={item.permissions['V']}
                      >
                        Validate
                      </Checkbox> */}
                      </div>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item
                      colspan={4}
                      style={{
                        ...styleCenter
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          handleRemoveRowStep(index);
                        }}
                        icon={<MinusIcon />}
                        size="xs"
                      />
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </List.Item>
              ))}
            </List>
            <IconButton
              onClick={handleAddRowStep}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />

            <Stack style={{ marginTop: 50, marginBottom: 10 }} spacing={5}>
              <TbSubtask /> <h6 className="title">Manage tasks permissions</h6>{' '}
            </Stack>
            <List hover>
              {permissionsTasks?.map((item, index) => (
                <List.Item key={index} index={index + 1}>
                  <FlexboxGrid>
                    <FlexboxGrid.Item colspan={6} style={styleCenter}>
                      <div style={{ textAlign: 'left' }}>
                        <div style={slimText}>Task</div>

                        <Form.Control
                          name="selectPicker"
                          accepter={SelectPicker}
                          data={tasks}
                          value={item?.task}
                          onChange={e => {
                            handleChangeTask(index, e);
                          }}
                          placement="auto"
                          style={{ width: 250 }}
                          disabledItemValues={
                            permissionsTasks && Array.from(permissionsTasks, ({ task }) => task)
                          }
                        />
                      </div>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item colspan={6} style={styleCenter}>
                      <div style={{ textAlign: 'left' }}>
                        <div style={slimText}>Step</div>
                        <p>
                          {
                            steps?.find(
                              val => val.value == tasks?.find(val => val.value == item.task)?.step
                            )?.label
                          }
                        </p>
                      </div>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item colspan={10} style={styleCenter}>
                      <div style={{ textAlign: 'left' }}>
                        <div style={slimText}>Access rights</div>
                        <Stack spacing={10}>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="R"
                            onChange={() => {
                              handleChangeTaskPermission(index, 'R');
                            }}
                            checked={item.permissions['R']}
                          >
                            Read
                          </Form.Control>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="W"
                            onChange={() => {
                              handleChangeTaskPermission(index, 'W');
                            }}
                            checked={item.permissions['W']}
                          >
                            Write
                          </Form.Control>
                          <Form.Control
                            name="checkbox"
                            accepter={Checkbox}
                            value="M"
                            onChange={() => {
                              handleChangeTaskPermission(index, 'M');
                            }}
                            checked={item.permissions['M']}
                          >
                            Manage owners
                          </Form.Control>
                        </Stack>

                        {/* <Checkbox
                      value="R"
                      onChange={() => {
                        handleChangeTaskPermission(index, 'R');
                      }}
                      checked={item.permissions['R']}
                    >
                      Read
                    </Checkbox>
                    <Checkbox
                      value="W"
                      onChange={() => {
                        handleChangeTaskPermission(index, 'W');
                      }}
                      checked={item.permissions['W']}
                    >
                      Write
                    </Checkbox>
                    <Checkbox
                      value="M"
                      onChange={() => {
                        handleChangeTaskPermission(index, 'M');
                      }}
                      checked={item.permissions['M']}
                    >
                      Manage owners
                    </Checkbox> */}
                      </div>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                      colspan={2}
                      style={{
                        ...styleCenter
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          handleRemoveRowTask(index);
                        }}
                        icon={<MinusIcon />}
                        size="xs"
                      />
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </List.Item>
              ))}
            </List>
            <IconButton
              onClick={handleAddRowTask}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </>
        )}
      </Form>

      <IconButton
        appearance="primary"
        icon={<Icon as={VscSave} />}
        style={{ float: 'right' }}
        onClick={handleSave}
        disabled={currentUser.admin != 'super'}
      >
        Save
      </IconButton>
    </>
  );
};

export default UserPermissions;
